import KeyboardDoubleArrowDownOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowDownOutlined';
import { Collapse, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import React, { Dispatch, ReactElement, SetStateAction, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';

import { NewEmailAttachments } from '@/components/emailWindow/components/attachments/NewEmailAttachments.comp.tsx';
import { NewEmailAiSuggestionButton } from '@/components/emailWindow/components/newEmail/components/aiSuggestionButton/NewEmailAiSuggestionButton.comp.tsx';
import { useInlineImageUploader } from '@/components/emailWindow/components/newEmail/util/useInlineImageUploader.util.ts';
import {
  mapEmailToSelectOption,
  NewEmailFormModel,
} from '@/components/emailWindow/model/NewEmailForm.model.ts';
import { NewEmailFormStyles } from '@/components/emailWindow/styles/NewEmail.styles';
import { EmailSelectOption } from '@/components/form/model/SelectSearchOption.model.ts';
import { RichTextFormField } from '@/components/form/RichTextFormField.comp.tsx';
import { SelectSearchFormField } from '@/components/form/SelectSearchFormField.comp.tsx';
import { TextFormField } from '@/components/form/TextFormField.comp.tsx';
import { themeColors } from '@/config/theme/themeColors.ts';
import { ToastConfig } from '@/config/ToastConfig.util.ts';
import { emailRegex } from '@/utils/regex.util.ts';
import { useHandleGetContactsSearch } from '@/utils/useHandleGetContactsSearch.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

interface Props {
  draftId: string | undefined;
  isSendingEmail: boolean;
}

export const NewEmailForm: React.FC<Props> = props => {
  const { draftId, isSendingEmail } = props;
  const inlineImageUploader = useInlineImageUploader(draftId);
  const { t } = useTranslationWithPrefix('views.emails.newEmail.body');
  const { handleGetContact } = useHandleGetContactsSearch();
  const [ccVisible, setCcVisible] = useState(false);
  const formMethods = useFormContext<NewEmailFormModel>();

  useEffect(() => {
    setCcVisible(formMethods.getValues('cc').length > 0);
  }, []);

  const handleCreateNewOption =
    (target: keyof Pick<NewEmailFormModel, 'bcc' | 'cc' | 'to'>) => (inputValue: string) => {
      if (!emailRegex.test(inputValue)) {
        toast(t('emailValidationFailed'), ToastConfig.warning);
        return;
      }
      const newOption: EmailSelectOption = mapEmailToSelectOption(inputValue);
      const currentValues = formMethods.getValues(target);
      currentValues.push(newOption);
      formMethods.setValue(target, currentValues);
    };

  const handleFormatCreateLabel = (inputValue: string): ReactElement => {
    if (emailRegex.test(inputValue)) {
      return <span>{inputValue}</span>;
    }
    return (
      <span>
        {inputValue} &emsp; &emsp; &emsp;
        <span style={{ color: 'red' }}>${t('emailValidationFailed')}</span>
      </span>
    );
  };

  return (
    <Stack spacing={2} sx={NewEmailFormStyles}>
      <Box sx={{ position: 'relative' }}>
        <SelectSearchFormField
          disabled={isSendingEmail}
          isAsync
          isCreatable
          isMulti
          label={t('to')}
          loadOptions={handleGetContact}
          name="to"
          onCreate={handleCreateNewOption('to')}
        />
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: 'center',
            bottom: -36,
            justifyContent: 'center',
            position: 'absolute',
            right: 0,
          }}
        >
          <NewEmailAiSuggestionButton
            emailId={draftId}
            isDisabled={formMethods.watch('to').length < 1}
          />
          <CcItem ccVisible={ccVisible} setCcVisible={setCcVisible} />
        </Stack>
      </Box>
      <Collapse in={ccVisible}>
        <Stack spacing={2} sx={{ pt: 2 }}>
          <SelectSearchFormField
            disabled={isSendingEmail}
            isAsync
            isCreatable
            isMulti
            label={t('cc')}
            loadOptions={handleGetContact}
            name="cc"
            onCreate={handleCreateNewOption('cc')}
          />
          <SelectSearchFormField
            disabled={isSendingEmail}
            formatCreateLabel={handleFormatCreateLabel}
            isAsync
            isCreatable
            isMulti
            label={t('bcc')}
            loadOptions={handleGetContact}
            name="bcc"
            onCreate={handleCreateNewOption('bcc')}
          />
        </Stack>
      </Collapse>
      <TextFormField disabled={isSendingEmail} label={t('subject')} name="subject" />
      <NewEmailAttachments draftId={draftId} />
      <RichTextFormField
        disabled={isSendingEmail}
        imageUploader={inlineImageUploader}
        label={t('body')}
        name="body"
      />
    </Stack>
  );
};

type CcItemProps = {
  ccVisible: boolean;
  setCcVisible: Dispatch<SetStateAction<boolean>>;
};

const CcItem: React.FC<CcItemProps> = ({ ccVisible, setCcVisible }) => {
  return (
    <Stack
      direction="row"
      onClick={() => setCcVisible(cur => !cur)}
      spacing={0.5}
      sx={{ alignItems: 'center', color: themeColors.greyDark, cursor: 'pointer' }}
    >
      CC
      <KeyboardDoubleArrowDownOutlinedIcon sx={{ transform: ccVisible ? 'rotate(180deg)' : '' }} />
    </Stack>
  );
};
